"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getSearchProduct, IProduct } from "../../server/product";
import { getListBrand } from "../../server/brand";
import { getCategoryTree } from "../../server/category/getTree";
import Breadcrumb from "../../components/Breadcrumb";
import ProductFilter from "../../components/ProductFilter";
import { formatVND } from "../../contants/formatVND";
import { getUserStatus, cleanUrl } from "../../contants";
import { Skeleton } from "../../components/Skeleton";

export default function ProductDetailPage() {
  return <ProductDetailPageContent />;
}

function ProductDetailPageContent() {
  const role = getUserStatus();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const key = searchParams.get("key") ?? "";
  const brandParam = searchParams.get("brandId") ?? searchParams.get("brandIds");
  const categoryParam =
    searchParams.get("categoryId") ?? searchParams.get("categoryIds");
  const inventoryParam = searchParams.get("inventoryStatus");

  const initialBrandIds = useMemo(() => {
    if (!brandParam) return [];
    return brandParam.split(",").filter(Boolean);
  }, [brandParam]);

  const initialCategoryIds = useMemo(() => {
    if (!categoryParam) return [];
    return categoryParam.split(",").filter(Boolean);
  }, [categoryParam]);

  const initialInventoryStatus = useMemo(() => {
    if (inventoryParam === null || inventoryParam === "") return null;
    const parsed = Number(inventoryParam);
    return Number.isNaN(parsed) ? null : parsed;
  }, [inventoryParam]);

  const [data, setData] = useState<IProduct[]>([]);
  const [total, setTotal] = useState(0);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(15);
  const [categoryIds, setCategoryIds] = useState<string[]>(initialCategoryIds);
  const [keyword, setKeyword] = useState(key);
  const [brandIds, setBrandIds] = useState<string[]>(initialBrandIds);
  const [inventoryStatus, setInventoryStatus] = useState<number | null>(
    initialInventoryStatus
  );

  const [dataBrand, setDataBrand] = useState<any[]>([]);
  const [dataCategory, setDataCategory] = useState<any[]>([]);
  const [load, setLoad] = useState(true);

  // ========== Load Brand + Category ===========
  useEffect(() => {
    async function loadBrandCategory() {
      const resBrand = await getListBrand();
      const resCategory = await getCategoryTree();

      setDataBrand(resBrand || []);
      setDataCategory(resCategory || []);
    }
    loadBrandCategory();
  }, [id]);

  useEffect(() => {
    setKeyword(key);
    setPageIndex(1);
  }, [key]);

  useEffect(() => {
    setBrandIds(initialBrandIds);
  }, [initialBrandIds]);

  useEffect(() => {
    setCategoryIds(initialCategoryIds);
  }, [initialCategoryIds]);

  useEffect(() => {
    setInventoryStatus(initialInventoryStatus);
  }, [initialInventoryStatus]);

  // ========== Load Product List ===========
  useEffect(() => {
    async function loadData() {
      setLoad(true);

      const body = {
        pageIndex,
        pageSize,
        orderBy: "",
        orderDirection: "",
        keyword,
        productCategoryIds: categoryIds,
        brandIds,
        inventoryStatus: inventoryStatus ?? undefined,
      };

      const res = await getSearchProduct(body);

      setData(res.data ?? []);
      setTotal(res.total ?? 0);
      setLoad(false);
    }

    loadData();
  }, [pageIndex, pageSize, keyword, brandIds, inventoryStatus, categoryIds]);

  // ========== Pagination Logic ===========
  const getPageList = useCallback((totalPages: number, current: number) => {
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);

      if (current > 4) pages.push("...");

      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (current < totalPages - 3) pages.push("...");

      pages.push(totalPages);
    }

    return pages;
  }, []);

  const totalPages = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  const pages = useMemo(
    () => getPageList(totalPages, pageIndex),
    [totalPages, pageIndex, getPageList]
  );

  return (
    <>
      <div className="max-w-7xl row pl-4" >
        <Breadcrumb
          items={[
            { label: "Trang chủ", href: "/" },
            { label: `Kết quả tìm kiếm "${key}"` },
          ]}
        />
      </div>

      <div className="row category-page-row max-w-7xl">
        <div className="col large-12">
          <div >

            <ProductFilter
              onChangeFilter={(filterValues: any) => {
                setKeyword(filterValues.keyword);
                setBrandIds(filterValues.brandIds);
                setInventoryStatus(filterValues.inventoryStatus);
                setCategoryIds(filterValues.categoryIds);

                setPageIndex(1);

                const params = new URLSearchParams();
                if (filterValues.keyword) params.set("key", filterValues.keyword);
                if (filterValues.brandIds?.length)
                  params.set("brandIds", filterValues.brandIds.join(","));
                if (filterValues.categoryIds?.length)
                  params.set("categoryIds", filterValues.categoryIds.join(","));
                if (
                  filterValues.inventoryStatus !== null &&
                  filterValues.inventoryStatus !== undefined &&
                  filterValues.inventoryStatus !== ""
                ) {
                  params.set(
                    "inventoryStatus",
                    String(filterValues.inventoryStatus)
                  );
                }
                setSearchParams(params);
              }}
              initialKeyword={keyword}
              initialBrandIds={initialBrandIds}
              initialCategoryIds={initialCategoryIds}
              initialInventoryStatus={initialInventoryStatus}
              keyword={keyword}
              dataBrand={dataBrand}
              dataCategory={dataCategory}
            />

            <div className="wcf-wpt-table-wrapper wcf-loop-template table-card p-3">
              <div className="woocommerce" />

              <table className="wc-product-table woocommerce table-modern">
                <thead>
                  <tr>
                    <th>Hình</th>
                    <th>Mã</th>
                    <th>Tên</th>
                    <th>Thương hiệu</th>
                    <th>Danh mục</th>
                    <th className="w-32">Tồn</th>
                    <th>Giá</th>
                  </tr>
                </thead>

                <tbody>
                  {load ? (
                    <TableSkeleton rows={5} cols={7} />
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-20">
                        <h1 className="text-2xl font-bold mb-4">
                          Không có sản phẩm nào
                        </h1>
                        <Link to="/" className="text-blue-600 hover:underline">
                          Quay lại trang chủ
                        </Link>
                      </td>
                    </tr>
                  ) : (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td className="w-32">
                          <Link to={`/chi-tiet-san-pham/${item.id}`}>
                            <img
                              src={cleanUrl(item.image || "") || "/no-image.jpg"}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/no-image.jpg";
                              }}
                              width={100}
                              height={100}
                              alt={item.productName}
                              className="rounded-lg border border-slate-200 object-cover"
                            /></Link>
                        </td>
                        <td>
                          <Link to={`/chi-tiet-san-pham/${item.id}`}>
                            {item.productCode}
                          </Link>
                        </td>

                        <td>
                          <Link to={`/chi-tiet-san-pham/${item.id}`}>
                            {item.productName}
                          </Link>
                        </td>

                        <td>{item.brandName}</td>
                        <td>{item.categoryName}</td>

                        <td>
                          {item.stockQuantity > 0 ? (
                            <span className="status-badge status-available">
                              Còn hàng
                            </span>
                          ) : (
                            <span className="status-badge status-out">
                              Hết hàng
                            </span>
                          )}
                        </td>

                        <td>
                          <b>{formatVND(item.price || 0)}</b>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* PAGINATION */}
              <div className="flex justify-center items-center gap-2 mt-6">
                <button
                  disabled={pageIndex === 1}
                  onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  Trước
                </button>

                {pages.map((p, i) => {
                  const active = p === pageIndex;
                  return (
                    <button
                      key={i}
                      disabled={p === "..."}
                      onClick={() => typeof p === "number" && setPageIndex(p)}
                      className={`rounded-full px-3 py-1 text-sm ${active
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                        }`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  disabled={pageIndex >= totalPages}
                  onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
                  className="rounded-full border border-slate-200 px-3 py-1 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  Sau
                </button>
              </div>

              <div className="text-center mt-3 text-sm text-slate-600">
                Tổng {total} sản phẩm
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function TableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr key={rowIdx}>
          {Array.from({ length: cols }).map((_, colIdx) => (
            <td key={colIdx} className="py-4">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
